// Normalize a user-picked image to a web-safe, reasonably-sized JPEG before
// upload. Solves the two real upload failures for phone users:
//   - iPhone photos are HEIC (backend rejects) — Safari can decode HEIC into an
//     <img>, so the canvas re-encode yields a JPEG that works everywhere.
//   - Photos over 8MB (backend cap) — downscaling + JPEG re-encode shrinks them.
// Falls back to the original File if the browser can't decode it (e.g. HEIC on
// Chrome) so the backend's clear error still surfaces.
export async function normalizeImage(file: File, maxDim = 2000, quality = 0.9): Promise<File> {
  // Already small and a web-safe type → skip the round trip.
  const webSafe = /^image\/(png|jpeg|webp|gif)$/.test(file.type);
  if (webSafe && file.size <= 3 * 1024 * 1024) return file;

  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.round(img.naturalWidth * scale);
    const h = Math.round(img.naturalHeight * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', quality),
    );
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch {
    return file; // undecodable (e.g. HEIC on Chrome) → let the backend explain
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('decode failed'));
    img.src = src;
  });
}
